import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  TextField,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';

import { AddCircle, Edit, SearchNormal1, Bag } from 'iconsax-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import CustomTable from 'components/@extended/CustomTable';
import SkeletonTable from 'components/SkeletonTable';
import IconButton from 'components/@extended/IconButton';

import { getAllSkillSectors, deleteSkillSector } from '../../services/skillSector.service';

const SkillSectorList = () => {
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);
  const [sortArray, setSortArray] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSkillSector, setSelectedSkillSector] = useState<any>(null);
  const fetchSkillSectors = async () => {
    try {
      setIsLoading(true);

      const params = {
        skip: pageIndex,
        limit: size,
        search
      };

      const res = await getAllSkillSectors(params);

      if (res?.status === 200) {
        setData(res?.data?.data?.skillSectors || res?.data?.data || []);
        setCount(res?.data?.data?.count || res?.data?.total || 0);
      }
    } catch (err) {
      console.error('Fetch skill sectors failed:', err);
      Snackbar('Failed to load skill sectors', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedSkillSector?._id) {
      console.warn('No _id found on selected skill sector', selectedSkillSector);
      Snackbar('Cannot delete: No sector selected', 'error');
      return;
    }

    const sectorId = selectedSkillSector._id;

    try {
      const response = await deleteSkillSector(sectorId);
      console.log('Delete response:', response?.status, response?.data);

      Snackbar('Skill Sector deleted successfully', 'success');

      setOpenDeleteDialog(false);
      setSelectedSkillSector(null);
      fetchSkillSectors();
    } catch (err: any) {
      console.error('Delete failed:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        fullError: err
      });

      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to delete skill sector (check console)';

      Snackbar(errorMsg, 'error');
    }
  };
  useEffect(() => {
    if (organization?._id) {
      fetchSkillSectors();
    }
  }, [organization?._id, pageIndex, size, search, sortArray]);

  // Sorting handler
  const handleSortChange = (column: any) => {
    const id = column.name || column.id || column.accessor;
    const index = sortArray.findIndex((item) => item.id === id);

    const next = [...sortArray];

    if (index === -1) {
      next.push({ id, sort: true, desc: false });
    } else if (!next[index].desc) {
      next[index].desc = true;
    } else {
      next.splice(index, 1);
    }

    setSortArray(next);
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedSkillSector(null);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: 'Sector Name',
        accessor: 'name'
      },

      {
        Header: 'Created By',
        accessor: (row: any) =>
          row?.createdBy ? (
            <Tooltip title={row.createdBy.email}>
              <span>
                {row.createdBy.firstName} {row.createdBy.lastName}
              </span>
            </Tooltip>
          ) : (
            '—'
          )
      },
      {
        Header: 'Updated By',
        accessor: (row: any) =>
          row?.updatedBy ? (
            <Tooltip title={row.updatedBy.email}>
              <span>
                {row.updatedBy.firstName} {row.updatedBy.lastName}
              </span>
            </Tooltip>
          ) : (
            '—'
          )
      },

      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const isAssignedToQP = row.original.isAssignedToQP === true;
          const isAdminUser = user?.type === 'admin';
          const editDisabled = !isAdminUser || isAssignedToQP;
          const deleteDisabled = !isAdminUser || isAssignedToQP;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip
                title={
                  !isAdminUser
                    ? 'Only admin can edit'
                    : isAssignedToQP
                    ? 'Assigned to QPs – cannot edit'
                    : 'Edit'
                }
              >
                <span>
                  <IconButton
                    color="primary"
                    disabled={editDisabled}
                    onClick={() =>
                      navigate('/skillSector/edit', {
                        state: { isEdit: true, data: row.original }
                      })
                    }
                  >
                    <Edit />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  !isAdminUser
                    ? 'Only admin can delete'
                    : isAssignedToQP
                    ? 'Assigned to QPs – cannot delete'
                    : 'Delete'
                }
              >
                <span>
                  <IconButton
                    color="error"
                    disabled={deleteDisabled}
                    onClick={() => {
                      setSelectedSkillSector(row.original);
                      setOpenDeleteDialog(true);
                    }}
                  >
                    <Bag />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    [navigate]
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1.5}>
        <MainCard
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <BackToggle show />
              <Box>
                <Typography variant="h5">
                  <FormattedMessage id="Skill Sector List" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage skill sectors and related skills
                </Typography>
              </Box>
            </Stack>
          }
          content={false}
          secondary={
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search sectors..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPageIndex(1);
                    setSearch(searchInput);
                  }
                }}
              />

              <IconButton
                variant="outlined"
                onClick={() => {
                  setPageIndex(1);
                  setSearch(searchInput);
                }}
                disabled={!searchInput}
              >
                <SearchNormal1 />
              </IconButton>

              {user?.type === 'admin' && (
                <IconButton variant="dashed" onClick={() => navigate('/skillSector/add')}>
                  <AddCircle />
                </IconButton>
              )}
            </Stack>
          }
        />

        <MainCard content={false}>
          {isLoading ? (
            <SkeletonTable columns={columns} />
          ) : (
            <CustomTable
              columns={columns}
              data={data}
              sortArray={['name']}
              count={count}
              size={size}
              pageIndex={pageIndex}
              sort={sortArray}
              setSortingData={handleSortChange}
              handlePageChange={(event: any, page: number) => setPageIndex(page)}
              handleSizeChange={(e: SelectChangeEvent<number>) => {
                setSize(Number(e.target.value));
                setPageIndex(1);
              }}
            />
          )}
        </MainCard>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Delete Skill Sector</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedSkillSector?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SkillSectorList;
